import { z } from "zod";
import { dateOnlySchema, listQuerySchema, tagsSchema, timestampSchema, uuidSchema } from "./api.ts";
import { blankToNull, timestampsShape } from "./common.ts";
import { gstStateCodeSchema } from "./india.ts";
import { currencyCodeSchema, minorSchema } from "./money.ts";

/**
 * PROPOSALS & QUOTES (PRD §8 Item 8, US-8.1…US-8.5).
 *
 * The HoneyBook/Dubsado loop: a sectioned document with PACKAGE OPTIONS the
 * client chooses between and ADD-ONS they tick, sent as a login-free link,
 * accepted with signature evidence, and followed by one orchestrated saga that
 * turns the "yes" into a client, a contract, an engagement, an invoice and a
 * project.
 *
 * Three rules the schemas below encode:
 *  · A proposal hangs off EXACTLY ONE of a lead or a client (a DB CHECK, not a
 *    convention) — PRD I-2 says money and contracts hang off the client, so a
 *    proposal on a lead must convert before anything downstream exists.
 *  · Every SENT version is frozen. Editing one creates v2 and supersedes v1, so
 *    what the client saw is always reconstructable.
 *  · `internalNotes` is on the staff DTO and on NO public schema in this file.
 */

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export const PROPOSAL_SECTION_KINDS = [
  "cover",
  "about_us",
  "understanding",
  "scope",
  "timeline",
  "team",
  "case_studies",
  "pricing",
  "terms",
  "custom",
] as const;
export type ProposalSectionKind = (typeof PROPOSAL_SECTION_KINDS)[number];
export const proposalSectionKindSchema = z.enum(PROPOSAL_SECTION_KINDS);

export const PROPOSAL_SECTION_LABELS: Record<ProposalSectionKind, string> = {
  cover: "Cover",
  about_us: "About us",
  understanding: "Our understanding of the brief",
  scope: "Scope and deliverables",
  timeline: "Timeline",
  team: "Team",
  case_studies: "Case studies",
  pricing: "Pricing",
  terms: "Terms",
  custom: "Custom section",
};

export const PROPOSAL_STATUSES = ["draft", "sent", "viewed", "accepted", "declined", "expired", "superseded"] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];
export const proposalStatusSchema = z.enum(PROPOSAL_STATUSES);

/** A proposal stops being editable the moment it leaves the building. */
export const PROPOSAL_EDITABLE_STATUSES: readonly ProposalStatus[] = ["draft"];

/** Statuses a client may still act on from the public link. */
export const PROPOSAL_OPEN_STATUSES: readonly ProposalStatus[] = ["sent", "viewed"];

export const PROPOSAL_OPTION_KINDS = ["option", "add_on"] as const;
export type ProposalOptionKind = (typeof PROPOSAL_OPTION_KINDS)[number];
export const proposalOptionKindSchema = z.enum(PROPOSAL_OPTION_KINDS);

export const PROPOSAL_EVENT_TYPES = ["sent", "viewed", "option_selected", "accepted", "declined", "changes_requested"] as const;
export type ProposalEventType = (typeof PROPOSAL_EVENT_TYPES)[number];
export const proposalEventTypeSchema = z.enum(PROPOSAL_EVENT_TYPES);

/** The saga that runs after "Accept". Visible to the AM, retryable step by step. */
export const ACCEPTANCE_RUN_STATUSES = ["pending", "running", "completed", "needs_attention", "cancelled"] as const;
export type AcceptanceRunStatus = (typeof ACCEPTANCE_RUN_STATUSES)[number];
export const acceptanceRunStatusSchema = z.enum(ACCEPTANCE_RUN_STATUSES);

export const ACCEPTANCE_STEPS = ["convert_lead", "create_contract", "create_engagement", "create_invoice", "create_project"] as const;
export type AcceptanceStepKey = (typeof ACCEPTANCE_STEPS)[number];
export const acceptanceStepKeySchema = z.enum(ACCEPTANCE_STEPS);

export const ACCEPTANCE_STEP_STATUSES = ["pending", "done", "skipped", "failed"] as const;
export type AcceptanceStepStatus = (typeof ACCEPTANCE_STEP_STATUSES)[number];
export const acceptanceStepStatusSchema = z.enum(ACCEPTANCE_STEP_STATUSES);

export const ACCEPTANCE_STEP_LABELS: Record<AcceptanceStepKey, string> = {
  convert_lead: "Convert the lead into a client",
  create_contract: "Create the contract",
  create_engagement: "Create the engagement",
  create_invoice: "Raise the advance invoice",
  create_project: "Create the onboarding project",
};

/** Default validity of a quote, in days (PRD §5 India defaults). */
export const PROPOSAL_DEFAULT_VALIDITY_DAYS = 15;
/** How long a public proposal link stays usable after it is sent. */
export const PROPOSAL_LINK_DEFAULT_DAYS = 60;

// ---------------------------------------------------------------------------
// Sections
// ---------------------------------------------------------------------------

/** One block of the document. `body` is markdown; `items` are the cards inside. */
export const proposalSectionSchema = z.object({
  key: z.string(),
  kind: proposalSectionKindSchema,
  title: z.string(),
  body: z.string().nullable(),
  items: z.array(z.object({ title: z.string(), body: z.string().nullable() })),
  visible: z.boolean(),
});
export type ProposalSection = z.infer<typeof proposalSectionSchema>;

const proposalSectionInputSchema = z
  .object({
    key: z.string().trim().min(1).max(60),
    kind: proposalSectionKindSchema,
    title: z.string().trim().min(1).max(200),
    body: blankToNull(z.string().max(20_000)).optional(),
    items: z
      .array(z.object({ title: z.string().trim().min(1).max(200), body: blankToNull(z.string().max(4000)).optional() }).strict())
      .max(20)
      .optional(),
    visible: z.boolean().optional(),
  })
  .strict();

// ---------------------------------------------------------------------------
// Templates
// ---------------------------------------------------------------------------

/** A reusable set of section blocks, versioned so a sent proposal can name the shape it came from. */
export const proposalTemplateSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  description: z.string().nullable(),
  sections: z.array(proposalSectionSchema),
  version: z.number().int(),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  /** The contract template the acceptance saga builds the contract from. */
  contractTemplateId: uuidSchema.nullable(),
  /** Does accepting this proposal open an onboarding project? (US-8.4) */
  createsOnboardingProject: z.boolean(),
  validityDays: z.number().int(),
  tags: z.array(z.string()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ProposalTemplateDto = z.infer<typeof proposalTemplateSchema>;

const templateWritableShape = {
  name: z.string().trim().min(1).max(200),
  description: blankToNull(z.string().max(2000)),
  sections: z.array(proposalSectionInputSchema).max(30),
  isDefault: z.boolean(),
  isActive: z.boolean(),
  contractTemplateId: uuidSchema.nullable(),
  createsOnboardingProject: z.boolean(),
  validityDays: z.number().int().min(1).max(365),
  tags: tagsSchema,
};

/** POST /api/v1/proposal-templates (perm `proposals:write`) → 201 ProposalTemplate. Duplicate name → CONFLICT. */
export const createProposalTemplateInputSchema = z
  .object({
    ...templateWritableShape,
    description: templateWritableShape.description.optional(),
    sections: templateWritableShape.sections.optional(),
    isDefault: templateWritableShape.isDefault.optional(),
    isActive: templateWritableShape.isActive.optional(),
    contractTemplateId: templateWritableShape.contractTemplateId.optional(),
    createsOnboardingProject: templateWritableShape.createsOnboardingProject.optional(),
    validityDays: templateWritableShape.validityDays.optional(),
    tags: templateWritableShape.tags.optional(),
  })
  .strict();
export type CreateProposalTemplateInput = z.input<typeof createProposalTemplateInputSchema>;
export type CreateProposalTemplateData = z.output<typeof createProposalTemplateInputSchema>;

/** PATCH /api/v1/proposal-templates/:id (perm `proposals:write`) → ProposalTemplate. Merge patch; `sections` replaced as a whole. */
export const updateProposalTemplateInputSchema = z.object(templateWritableShape).partial().strict();
export type UpdateProposalTemplateInput = z.input<typeof updateProposalTemplateInputSchema>;
export type UpdateProposalTemplateData = z.output<typeof updateProposalTemplateInputSchema>;

// ---------------------------------------------------------------------------
// Pricing options, add-ons and lines
// ---------------------------------------------------------------------------

export const proposalLineSchema = z.object({
  id: uuidSchema,
  optionId: uuidSchema,
  serviceId: uuidSchema.nullable(),
  description: z.string(),
  sacCode: z.string().nullable(),
  quantityMilli: z.number().int(),
  unitPriceMinor: minorSchema,
  discountMinor: minorSchema,
  taxRateBps: z.number().int(),
  taxableMinor: minorSchema,
  taxMinor: minorSchema,
  totalMinor: minorSchema,
  position: z.number().int(),
});
export type ProposalLineDto = z.infer<typeof proposalLineSchema>;

/** What the acceptance saga should do with the option the client picked (US-8.4). */
export const optionOutcomeSchema = z.object({
  engagementType: z.enum(["retainer", "fixed_fee", "time_and_materials"]),
  billingFrequency: z.enum(["monthly", "quarterly", "half_yearly", "yearly", "milestone"]),
  deliverableQuotas: z.array(z.object({ type: z.string(), qty: z.number().int(), rollover: z.enum(["none", "one_period", "unlimited"]) })),
  termMonths: z.number().int().nullable(),
  /** Percentage (bps) of the accepted total to invoice up front; 0 = none. */
  advancePercentBps: z.number().int(),
  advanceDocType: z.enum(["INV", "PI"]),
});
export type OptionOutcome = z.infer<typeof optionOutcomeSchema>;

export const proposalOptionSchema = z.object({
  id: uuidSchema,
  proposalId: uuidSchema,
  kind: proposalOptionKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  packageId: uuidSchema.nullable(),
  isRecommended: z.boolean(),
  position: z.number().int(),
  currency: currencyCodeSchema,
  subtotalMinor: minorSchema,
  discountMinor: minorSchema,
  taxableMinor: minorSchema,
  cgstMinor: minorSchema,
  sgstMinor: minorSchema,
  igstMinor: minorSchema,
  taxMinor: minorSchema,
  totalMinor: minorSchema,
  outcome: optionOutcomeSchema,
  lines: z.array(proposalLineSchema),
});
export type ProposalOptionDto = z.infer<typeof proposalOptionSchema>;

const proposalLineInputSchema = z
  .object({
    serviceId: uuidSchema.nullable().optional(),
    description: z.string().trim().min(1).max(500),
    sacCode: blankToNull(z.string().trim().regex(/^\d{4,8}$/, "SAC must be 4–8 digits")).optional(),
    quantityMilli: z.number().int().min(1).max(1_000_000_000).optional(),
    unitPriceMinor: minorSchema.min(0),
    discountMinor: minorSchema.min(0).optional(),
    taxRateBps: z.number().int().min(0).max(10_000).optional(),
  })
  .strict();
export type ProposalLineInput = z.input<typeof proposalLineInputSchema>;

const optionOutcomeInputSchema = z
  .object({
    engagementType: optionOutcomeSchema.shape.engagementType.optional(),
    billingFrequency: optionOutcomeSchema.shape.billingFrequency.optional(),
    deliverableQuotas: z
      .array(
        z
          .object({
            type: z.string().trim().min(1).max(80),
            qty: z.number().int().min(0).max(10_000),
            rollover: z.enum(["none", "one_period", "unlimited"]).optional(),
          })
          .strict(),
      )
      .max(30)
      .optional(),
    termMonths: z.number().int().min(1).max(120).nullable().optional(),
    advancePercentBps: z.number().int().min(0).max(10_000).optional(),
    advanceDocType: z.enum(["INV", "PI"]).optional(),
  })
  .strict();

const optionWritableShape = {
  kind: proposalOptionKindSchema,
  name: z.string().trim().min(1).max(160),
  description: blankToNull(z.string().max(2000)),
  packageId: uuidSchema.nullable(),
  isRecommended: z.boolean(),
  position: z.number().int().min(0).max(1000),
  outcome: optionOutcomeInputSchema,
  lines: z.array(proposalLineInputSchema).max(100),
};

/** POST /api/v1/proposals/:id/options (perm `proposals:write`) → 201 ProposalOption. Draft only → 409. */
export const createProposalOptionInputSchema = z
  .object({
    ...optionWritableShape,
    kind: optionWritableShape.kind.optional(),
    description: optionWritableShape.description.optional(),
    packageId: optionWritableShape.packageId.optional(),
    isRecommended: optionWritableShape.isRecommended.optional(),
    position: optionWritableShape.position.optional(),
    outcome: optionWritableShape.outcome.optional(),
    lines: optionWritableShape.lines.optional(),
  })
  .strict();
export type CreateProposalOptionInput = z.input<typeof createProposalOptionInputSchema>;
export type CreateProposalOptionData = z.output<typeof createProposalOptionInputSchema>;

/** PATCH /api/v1/proposals/:id/options/:optionId (perm `proposals:write`). Merge patch; `lines` replaced as a whole. */
export const updateProposalOptionInputSchema = z.object(optionWritableShape).partial().strict();
export type UpdateProposalOptionInput = z.input<typeof updateProposalOptionInputSchema>;
export type UpdateProposalOptionData = z.output<typeof updateProposalOptionInputSchema>;

// ---------------------------------------------------------------------------
// The proposal
// ---------------------------------------------------------------------------

export const proposalSchema = z.object({
  id: uuidSchema,
  number: z.string(),
  version: z.number().int(),
  rootId: uuidSchema,
  supersededById: uuidSchema.nullable(),
  leadId: uuidSchema.nullable(),
  clientId: uuidSchema.nullable(),
  templateId: uuidSchema.nullable(),
  contractTemplateId: uuidSchema.nullable(),
  createsOnboardingProject: z.boolean(),
  ownerId: uuidSchema.nullable(),
  title: z.string(),
  status: proposalStatusSchema,
  sections: z.array(proposalSectionSchema),
  currency: currencyCodeSchema,
  validityDate: dateOnlySchema.nullable(),
  placeOfSupplyStateCode: z.string().nullable(),
  placeOfSupplyCountry: z.string(),
  supplyType: z.enum(["intra_state", "inter_state", "export"]),
  /** Never leaves the workspace — no public schema in this file carries it. */
  internalNotes: z.string().nullable(),
  recipientContactId: uuidSchema.nullable(),
  recipientEmail: z.string().nullable(),
  selectedOptionId: uuidSchema.nullable(),
  selectedAddOnIds: z.array(uuidSchema),
  acceptedTotalMinor: minorSchema.nullable(),
  declineReason: z.string().nullable(),
  sentAt: timestampSchema.nullable(),
  viewedAt: timestampSchema.nullable(),
  viewCount: z.number().int(),
  decidedAt: timestampSchema.nullable(),
  decidedByContactId: uuidSchema.nullable(),
  decidedByName: z.string().nullable(),
  publicUrl: z.string().nullable(),
  publicLinkExpiresAt: timestampSchema.nullable(),
  options: z.array(proposalOptionSchema),
  tags: z.array(z.string()),
  customFields: z.record(z.string(), z.unknown()),
  archivedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type ProposalDto = z.infer<typeof proposalSchema>;

/** The row shape lists and pickers use — no sections, no lines. */
export const proposalSummarySchema = z.object({
  id: uuidSchema,
  number: z.string(),
  version: z.number().int(),
  title: z.string(),
  status: proposalStatusSchema,
  leadId: uuidSchema.nullable(),
  clientId: uuidSchema.nullable(),
  ownerId: uuidSchema.nullable(),
  currency: currencyCodeSchema,
  totalMinor: minorSchema,
  validityDate: dateOnlySchema.nullable(),
  sentAt: timestampSchema.nullable(),
  viewedAt: timestampSchema.nullable(),
  decidedAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type ProposalSummaryDto = z.infer<typeof proposalSummarySchema>;

const proposalWritableShape = {
  title: z.string().trim().min(1).max(200),
  sections: z.array(proposalSectionInputSchema).max(30),
  currency: currencyCodeSchema,
  validityDate: dateOnlySchema.nullable(),
  placeOfSupplyStateCode: gstStateCodeSchema.nullable(),
  placeOfSupplyCountry: z.string().trim().toUpperCase().length(2),
  internalNotes: blankToNull(z.string().max(10_000)),
  ownerId: uuidSchema.nullable(),
  recipientContactId: uuidSchema.nullable(),
  recipientEmail: blankToNull(z.string().trim().toLowerCase().email().max(254)),
  contractTemplateId: uuidSchema.nullable(),
  createsOnboardingProject: z.boolean(),
  tags: tagsSchema,
  customFields: z.record(z.string(), z.unknown()),
};

/**
 * POST /api/v1/proposals (perm `proposals:write`) → 201 Proposal.
 * EXACTLY ONE of `leadId` / `clientId` — both or neither answers 422, and the
 * database CHECK says the same thing so no path can get around it.
 */
export const createProposalInputSchema = z
  .object({
    leadId: uuidSchema.optional(),
    clientId: uuidSchema.optional(),
    templateId: uuidSchema.optional(),
    /** Seeds the first "option" from a catalog package. */
    packageId: uuidSchema.optional(),
    ...proposalWritableShape,
    title: proposalWritableShape.title.optional(),
    sections: proposalWritableShape.sections.optional(),
    currency: proposalWritableShape.currency.optional(),
    validityDate: proposalWritableShape.validityDate.optional(),
    placeOfSupplyStateCode: proposalWritableShape.placeOfSupplyStateCode.optional(),
    placeOfSupplyCountry: proposalWritableShape.placeOfSupplyCountry.optional(),
    internalNotes: proposalWritableShape.internalNotes.optional(),
    ownerId: proposalWritableShape.ownerId.optional(),
    recipientContactId: proposalWritableShape.recipientContactId.optional(),
    recipientEmail: proposalWritableShape.recipientEmail.optional(),
    contractTemplateId: proposalWritableShape.contractTemplateId.optional(),
    createsOnboardingProject: proposalWritableShape.createsOnboardingProject.optional(),
    tags: proposalWritableShape.tags.optional(),
    customFields: proposalWritableShape.customFields.optional(),
  })
  .strict();
export type CreateProposalInput = z.input<typeof createProposalInputSchema>;
export type CreateProposalData = z.output<typeof createProposalInputSchema>;

/** PATCH /api/v1/proposals/:id (perm `proposals:write`) → Proposal. DRAFT ONLY — a sent version is frozen (409). */
export const updateProposalInputSchema = z.object(proposalWritableShape).partial().strict();
export type UpdateProposalInput = z.input<typeof updateProposalInputSchema>;
export type UpdateProposalData = z.output<typeof updateProposalInputSchema>;

export const listProposalsQuerySchema = listQuerySchema.extend({
  status: proposalStatusSchema.optional(),
  clientId: uuidSchema.optional(),
  leadId: uuidSchema.optional(),
  ownerId: uuidSchema.optional(),
  sort: z.enum(["createdAt", "number", "validityDate", "totalMinor"]).optional(),
});
export type ListProposalsQuery = z.infer<typeof listProposalsQuerySchema>;

/** POST /api/v1/proposals/:id/send (perm `proposals:write`) → Proposal, status `sent` and frozen. */
export const sendProposalInputSchema = z
  .object({
    to: z.string().trim().toLowerCase().email().max(254).optional(),
    message: z.string().trim().max(4000).optional(),
    linkDays: z.number().int().min(1).max(365).optional(),
  })
  .strict();
export type SendProposalInput = z.input<typeof sendProposalInputSchema>;
export type SendProposalData = z.output<typeof sendProposalInputSchema>;

/** POST /api/v1/proposals/:id/versions (perm `proposals:write`) → 201 the new draft v(n+1); v(n) becomes `superseded`. */
export const createProposalVersionInputSchema = z.object({ reason: z.string().trim().max(500).optional() }).strict();
export type CreateProposalVersionInput = z.input<typeof createProposalVersionInputSchema>;

/** POST /api/v1/proposals/:id/accept (perm `proposals:write`) → Proposal. Accepts on the client's behalf and starts the saga. */
export const acceptProposalInputSchema = z
  .object({
    optionId: uuidSchema.optional(),
    addOnIds: z.array(uuidSchema).max(30).optional(),
    signerName: z.string().trim().min(1).max(160).optional(),
    signerEmail: z.string().trim().toLowerCase().email().max(254).optional(),
  })
  .strict();
export type AcceptProposalInput = z.input<typeof acceptProposalInputSchema>;
export type AcceptProposalData = z.output<typeof acceptProposalInputSchema>;

/** The view/accept audit trail — the signature evidence of US-8.3. */
export const proposalEventSchema = z.object({
  id: uuidSchema,
  proposalId: uuidSchema,
  type: proposalEventTypeSchema,
  contactId: uuidSchema.nullable(),
  actorName: z.string().nullable(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  occurredAt: timestampSchema,
});
export type ProposalEventDto = z.infer<typeof proposalEventSchema>;

// ---------------------------------------------------------------------------
// The acceptance saga (US-8.4)
// ---------------------------------------------------------------------------

export const acceptanceStepSchema = z.object({
  key: acceptanceStepKeySchema,
  label: z.string(),
  status: acceptanceStepStatusSchema,
  resultId: uuidSchema.nullable(),
  error: z.string().nullable(),
  attempts: z.number().int(),
  finishedAt: timestampSchema.nullable(),
});
export type AcceptanceStepDto = z.infer<typeof acceptanceStepSchema>;

export const acceptanceRunSchema = z.object({
  id: uuidSchema,
  proposalId: uuidSchema,
  status: acceptanceRunStatusSchema,
  steps: z.array(acceptanceStepSchema),
  clientId: uuidSchema.nullable(),
  contractId: uuidSchema.nullable(),
  engagementId: uuidSchema.nullable(),
  invoiceId: uuidSchema.nullable(),
  projectId: uuidSchema.nullable(),
  lastError: z.string().nullable(),
  startedAt: timestampSchema,
  finishedAt: timestampSchema.nullable(),
  ...timestampsShape,
});
export type AcceptanceRunDto = z.infer<typeof acceptanceRunSchema>;

/** POST /api/v1/proposals/:id/acceptance-run/retry (perm `proposals:write`) → AcceptanceRun. */
export const retryAcceptanceRunInputSchema = z.object({}).strict();
export type RetryAcceptanceRunInput = z.input<typeof retryAcceptanceRunInputSchema>;

/** POST /api/v1/proposals/:id/acceptance-run/skip (perm `proposals:write`) → AcceptanceRun with that step `skipped`. */
export const skipAcceptanceStepInputSchema = z.object({ step: acceptanceStepKeySchema }).strict();
export type SkipAcceptanceStepInput = z.input<typeof skipAcceptanceStepInputSchema>;

// ---------------------------------------------------------------------------
// The public (login-free) surface — US-8.2, US-8.3
// ---------------------------------------------------------------------------

/** Path param of every `/api/public/v1/proposals/:token` route. */
export const publicProposalTokenParamsSchema = z.object({ token: z.string().min(40).max(200) });
export type PublicProposalTokenParams = z.infer<typeof publicProposalTokenParamsSchema>;

export const publicProposalLineSchema = z.object({
  description: z.string(),
  sacCode: z.string().nullable(),
  quantityMilli: z.number().int(),
  unitPriceMinor: minorSchema,
  discountMinor: minorSchema,
  taxRateBps: z.number().int(),
  taxableMinor: minorSchema,
  totalMinor: minorSchema,
});

export const publicProposalOptionSchema = z.object({
  id: uuidSchema,
  kind: proposalOptionKindSchema,
  name: z.string(),
  description: z.string().nullable(),
  isRecommended: z.boolean(),
  currency: currencyCodeSchema,
  subtotalMinor: minorSchema,
  discountMinor: minorSchema,
  taxableMinor: minorSchema,
  cgstMinor: minorSchema,
  sgstMinor: minorSchema,
  igstMinor: minorSchema,
  taxMinor: minorSchema,
  totalMinor: minorSchema,
  lines: z.array(publicProposalLineSchema),
});

/** What the client sees. Deliberately narrower than `proposalSchema` — no internal notes, no ids of ours. */
export const publicProposalViewSchema = z.object({
  number: z.string(),
  version: z.number().int(),
  title: z.string(),
  status: proposalStatusSchema,
  agencyName: z.string(),
  agencyLogoUrl: z.string().nullable(),
  brandColor: z.string().nullable(),
  clientName: z.string().nullable(),
  sections: z.array(proposalSectionSchema),
  currency: currencyCodeSchema,
  validityDate: dateOnlySchema.nullable(),
  expired: z.boolean(),
  placeOfSupply: z.string().nullable(),
  options: z.array(publicProposalOptionSchema),
  addOns: z.array(publicProposalOptionSchema),
  selectedOptionId: uuidSchema.nullable(),
  selectedAddOnIds: z.array(uuidSchema),
  acceptedTotalMinor: minorSchema.nullable(),
  decidedAt: timestampSchema.nullable(),
  decidedByName: z.string().nullable(),
  /** Set once accepted and a contract has been produced, so the client can sign next. */
  contractUrl: z.string().nullable(),
});
export type PublicProposalViewDto = z.infer<typeof publicProposalViewSchema>;

/** POST /api/public/v1/proposals/:token/select — the client picks an option and ticks add-ons. */
export const publicSelectOptionInputSchema = z
  .object({ optionId: uuidSchema, addOnIds: z.array(uuidSchema).max(30).optional() })
  .strict();
export type PublicSelectOptionInput = z.input<typeof publicSelectOptionInputSchema>;
export type PublicSelectOptionData = z.output<typeof publicSelectOptionInputSchema>;

/**
 * POST /api/public/v1/proposals/:token/accept — the client accepts.
 * Idempotent: a double submit returns the same view and starts exactly one saga.
 */
export const publicAcceptProposalInputSchema = z
  .object({
    optionId: uuidSchema,
    addOnIds: z.array(uuidSchema).max(30).optional(),
    signerName: z.string().trim().min(1).max(160),
    signerEmail: z.string().trim().toLowerCase().email().max(254),
    signatureText: z.string().trim().max(160).optional(),
    acceptedTerms: z.literal(true),
  })
  .strict();
export type PublicAcceptProposalInput = z.input<typeof publicAcceptProposalInputSchema>;
export type PublicAcceptProposalData = z.output<typeof publicAcceptProposalInputSchema>;

/** POST /api/public/v1/proposals/:token/decline — declining, with the reason the AM will see as a lost reason. */
export const publicDeclineProposalInputSchema = z
  .object({ reason: z.string().trim().max(1000).optional(), signerName: z.string().trim().max(160).optional() })
  .strict();
export type PublicDeclineProposalInput = z.input<typeof publicDeclineProposalInputSchema>;
export type PublicDeclineProposalData = z.output<typeof publicDeclineProposalInputSchema>;
